import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type AriaAttributes,
  type FocusEvent,
  type SyntheticEvent,
} from 'react';
import type { FieldRule } from '../lib/validation/field-rule';

type Control = HTMLInputElement | HTMLTextAreaElement;

/** The control's own props the hook wraps rather than replaces. */
interface Handlers<E extends Control> {
  /** An error the form reports itself (e.g. a cross-field problem shown under the field). */
  'aria-invalid'?: AriaAttributes['aria-invalid'];
  onBlur?: ((event: FocusEvent<E>) => void) | undefined;
  onInvalid?: ((event: SyntheticEvent<E>) => void) | undefined;
}

/**
 * Validates one input or textarea as the user types. The rule's message becomes the control's
 * custom validity, so forms block on it like any native constraint, and the message shows
 * inline once the user has left the field or tried to submit.
 */
export function useFieldValidity<E extends Control>(
  rule: FieldRule | undefined,
  value: unknown,
  handlers: Handlers<E>,
) {
  const ref = useRef<E>(null);
  const errorId = useId();
  const [touched, setTouched] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const raw = typeof value === 'number' ? String(value) : value;
  const ruleMessage = rule && typeof raw === 'string' ? rule.check(raw) : null;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.setCustomValidity(ruleMessage ?? '');
    setMessage(el.validity.valid ? null : el.validationMessage);
  }, [ruleMessage, value]);

  const shown = touched ? message : null;
  return {
    ref,
    shown,
    errorId,
    /** The rule's native limits; explicit props on the control override them. */
    attrs: rule?.attrs ?? {},
    /** Spread after the control's own props: they wrap its onBlur / onInvalid. */
    controlProps: {
      'aria-invalid': shown ? true : handlers['aria-invalid'],
      'aria-describedby': shown ? errorId : undefined,
      onBlur(event: FocusEvent<E>) {
        setTouched(true);
        handlers.onBlur?.(event);
      },
      onInvalid(event: SyntheticEvent<E>) {
        // The inline message replaces the browser's bubble.
        event.preventDefault();
        setTouched(true);
        setMessage(event.currentTarget.validationMessage);
        handlers.onInvalid?.(event);
      },
    },
  };
}
